import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  DocumentTextIcon,
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";

// Step indicator component
const StepIndicator: React.FC<{
  steps: string[];
  currentStep: number;
  isSpanish: boolean;
}> = ({ steps, currentStep }) => (
  <div className="flex items-center justify-between mb-4 px-2">
    {steps.map((step, index) => (
      <React.Fragment key={step}>
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0.5 }}
          animate={{
            opacity: index <= currentStep ? 1 : 0.5,
            scale: index === currentStep ? 1.1 : 1,
          }}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              index < currentStep
                ? "bg-atlantis-500 text-white"
                : index === currentStep
                  ? "bg-tiber-500 text-white"
                  : "bg-white/20 text-white/60"
            }`}
          >
            {index < currentStep ? "✓" : index + 1}
          </div>
          <span className="text-[10px] text-white/70 mt-1 text-center max-w-[60px] leading-tight">
            {step}
          </span>
        </motion.div>
        {index < steps.length - 1 && (
          <div
            className={`flex-1 h-0.5 mx-1 transition-colors ${
              index < currentStep ? "bg-atlantis-500" : "bg-white/20"
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Typing indicator component
const TypingIndicator: React.FC = () => (
  <div className="flex items-center gap-1 px-3 py-2">
    <motion.div
      className="w-2 h-2 bg-tiber-500 rounded-full"
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.8, repeat: Infinity, delay: 0 }}
    />
    <motion.div
      className="w-2 h-2 bg-tiber-500 rounded-full"
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.8, repeat: Infinity, delay: 0.15 }}
    />
    <motion.div
      className="w-2 h-2 bg-tiber-500 rounded-full"
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.8, repeat: Infinity, delay: 0.3 }}
    />
  </div>
);

// Time comparison card
const TimeComparison: React.FC<{ isSpanish: boolean }> = ({ isSpanish }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/10"
  >
    <div className="flex items-center gap-2 mb-2">
      <ClockIcon className="w-4 h-4 text-dorado" />
      <span className="text-white/80 text-xs font-medium">
        {isSpanish ? "Tiempo ahorrado" : "Time saved"}
      </span>
    </div>
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="text-white/50 text-[10px] mb-1">
          {isSpanish ? "Tradicional" : "Traditional"}
        </div>
        <div className="bg-red-500/20 rounded-full h-2 w-full" />
        <div className="text-red-300 text-[10px] mt-0.5">2-3 hrs</div>
      </div>
      <div className="flex-1">
        <div className="text-white/50 text-[10px] mb-1">Lawgentia</div>
        <motion.div
          className="bg-atlantis-500 rounded-full h-2"
          initial={{ width: 0 }}
          animate={{ width: "15%" }}
          transition={{ duration: 0.5, delay: 0.3 }}
        />
        <div className="text-green-300 text-[10px] mt-0.5">~30s</div>
      </div>
    </div>
  </motion.div>
);

// Form field animation
const FormField: React.FC<{
  label: string;
  value: string;
  delay: number;
}> = ({ label, value, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay }}
    className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0"
  >
    <span className="text-white/60 text-xs">{label}</span>
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: delay + 0.2 }}
      className="text-white text-xs font-medium"
    >
      {value}
    </motion.span>
  </motion.div>
);

// Document preview animation
const DocumentPreview: React.FC<{ isSpanish: boolean }> = ({ isSpanish }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-lg p-3 shadow-xl"
  >
    <div className="flex items-start gap-2 mb-2">
      <DocumentCheckIcon className="w-5 h-5 text-atlantis-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-tiber-800 text-xs font-semibold truncate">
          contrato-trabajo-rd.pdf
        </p>
        <p className="text-titan text-[10px]">
          {isSpanish ? "Listo para descargar" : "Ready to download"}
        </p>
      </div>
    </div>
    {/* Simulated document lines */}
    <div className="space-y-1.5 mt-3">
      <div className="h-1.5 bg-gray-200 rounded-full w-full" />
      <div className="h-1.5 bg-gray-200 rounded-full w-4/5" />
      <div className="h-1.5 bg-gray-200 rounded-full w-full" />
      <div className="h-1.5 bg-gray-200 rounded-full w-3/5" />
    </div>
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="w-full mt-3 bg-tiber-500 text-white text-xs font-medium py-2 rounded-lg flex items-center justify-center gap-1"
    >
      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
      {isSpanish ? "Descargar PDF" : "Download PDF"}
    </motion.button>
  </motion.div>
);

type DemoPhase =
  | "idle"
  | "select_type"
  | "select_country"
  | "filling_form"
  | "generating"
  | "complete";

export const HeroChatDemo: React.FC = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === "es";

  const [phase, setPhase] = useState<DemoPhase>("idle");
  const [showTyping, setShowTyping] = useState(false);

  const steps = isSpanish
    ? ["Tipo", "País", "Datos", "Generar"]
    : ["Type", "Country", "Data", "Generate"];

  const getCurrentStep = useCallback(() => {
    switch (phase) {
      case "idle":
        return -1;
      case "select_type":
        return 0;
      case "select_country":
        return 1;
      case "filling_form":
        return 2;
      case "generating":
        return 3;
      case "complete":
        return 4;
      default:
        return -1;
    }
  }, [phase]);

  // Demo flow controller
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    const runDemo = () => {
      // Start after 500ms
      timers.push(
        setTimeout(() => {
          setPhase("select_type");
        }, 500)
      );

      // Select country after 1.5s
      timers.push(
        setTimeout(() => {
          setPhase("select_country");
        }, 1800)
      );

      // Start filling form after 3s
      timers.push(
        setTimeout(() => {
          setPhase("filling_form");
        }, 3200)
      );

      // Show generating after 5.5s
      timers.push(
        setTimeout(() => {
          setShowTyping(true);
          setPhase("generating");
        }, 5800)
      );

      // Complete after 7.5s
      timers.push(
        setTimeout(() => {
          setShowTyping(false);
          setPhase("complete");
        }, 7800)
      );

      // Reset after 12s
      timers.push(
        setTimeout(() => {
          setPhase("idle");
          runDemo();
        }, 12000)
      );
    };

    runDemo();

    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const formFields = isSpanish
    ? [
        { label: "Empleador", value: "Empresa ABC SRL" },
        { label: "Empleado", value: "Juan Pérez" },
        { label: "Cargo", value: "Desarrollador Senior" },
        { label: "Salario", value: "RD$85,000/mes" },
      ]
    : [
        { label: "Employer", value: "ABC Company LLC" },
        { label: "Employee", value: "John Smith" },
        { label: "Position", value: "Senior Developer" },
        { label: "Salary", value: "$4,500/month" },
      ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="relative"
    >
      {/* Floating badges */}
      <motion.div
        className="absolute -top-4 -right-4 bg-tiber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg z-20 hidden md:flex items-center gap-1"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <SparklesIcon className="w-3 h-3" />
        AI Powered
      </motion.div>

      <motion.div
        className="absolute -bottom-3 -left-3 bg-dorado text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg z-20 hidden md:flex items-center gap-1"
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
      >
        ⚡ 30s
      </motion.div>

      {/* Main container */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-tiber-800/50 px-4 py-3 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-linear-to-br from-tiber-500 to-dorado flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">
              {isSpanish ? "Generador de Contratos" : "Contract Generator"}
            </p>
            <p className="text-white/60 text-xs">
              {isSpanish
                ? "Crea documentos legales en segundos"
                : "Create legal documents in seconds"}
            </p>
          </div>
        </div>

        {/* Content area */}
        <div className="p-4 min-h-[300px]">
          {/* Step indicator */}
          <StepIndicator
            steps={steps}
            currentStep={getCurrentStep()}
            isSpanish={isSpanish}
          />

          <AnimatePresence mode="wait">
            {/* Step 1: Select Document Type */}
            {phase === "select_type" && (
              <motion.div
                key="select_type"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-2"
              >
                <p className="text-white/80 text-xs mb-3">
                  {isSpanish
                    ? "¿Qué documento necesitas?"
                    : "What document do you need?"}
                </p>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-tiber-500/20 border-2 border-tiber-500 rounded-xl p-3 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-tiber-500" />
                    <div>
                      <p className="text-white text-sm font-medium">
                        {isSpanish
                          ? "Contrato de Trabajo"
                          : "Employment Contract"}
                      </p>
                      <p className="text-white/60 text-[10px]">
                        {isSpanish
                          ? "Adaptado al código laboral"
                          : "Adapted to labor code"}
                      </p>
                    </div>
                  </div>
                </motion.div>
                <div className="bg-white/5 rounded-xl p-3 opacity-50">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-white/50" />
                    <p className="text-white/50 text-sm">
                      {isSpanish ? "Contrato de Arriendo" : "Rental Agreement"}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Select Country */}
            {phase === "select_country" && (
              <motion.div
                key="select_country"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-2"
              >
                <p className="text-white/80 text-xs mb-3">
                  {isSpanish ? "¿Para qué país?" : "For which country?"}
                </p>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-tiber-500/20 border-2 border-tiber-500 rounded-xl p-3 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🇩🇴</span>
                    <div>
                      <p className="text-white text-sm font-medium">
                        {isSpanish
                          ? "República Dominicana"
                          : "Dominican Republic"}
                      </p>
                      <p className="text-white/60 text-[10px]">
                        {isSpanish
                          ? "Código de Trabajo vigente"
                          : "Current Labor Code"}
                      </p>
                    </div>
                  </div>
                </motion.div>
                <div className="flex gap-2">
                  {["🇲🇽", "🇨🇴", "🇨🇱", "🇵🇪"].map((flag) => (
                    <div
                      key={flag}
                      className="flex-1 bg-white/5 rounded-lg p-2 text-center opacity-50"
                    >
                      <span className="text-lg">{flag}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Filling Form */}
            {phase === "filling_form" && (
              <motion.div
                key="filling_form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <p className="text-white/80 text-xs mb-3">
                  {isSpanish ? "Datos del contrato:" : "Contract details:"}
                </p>
                <div className="bg-white/5 rounded-xl p-3 space-y-0">
                  {formFields.map((field, index) => (
                    <FormField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      delay={index * 0.3}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Generating */}
            {phase === "generating" && (
              <motion.div
                key="generating"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col items-center justify-center py-6"
              >
                <motion.div
                  className="w-16 h-16 rounded-full bg-tiber-500/20 flex items-center justify-center mb-4"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <SparklesIcon className="w-8 h-8 text-tiber-500" />
                </motion.div>
                <p className="text-white font-medium text-sm">
                  {isSpanish
                    ? "Generando documento..."
                    : "Generating document..."}
                </p>
                <p className="text-white/60 text-xs mt-1">
                  {isSpanish
                    ? "IA adaptando al marco legal"
                    : "AI adapting to legal framework"}
                </p>
                {showTyping && <TypingIndicator />}
              </motion.div>
            )}

            {/* Step 5: Complete */}
            {phase === "complete" && (
              <motion.div
                key="complete"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                  <span className="text-green-300 font-medium text-sm">
                    {isSpanish ? "¡Documento listo!" : "Document ready!"}
                  </span>
                </div>
                <DocumentPreview isSpanish={isSpanish} />
                <TimeComparison isSpanish={isSpanish} />
              </motion.div>
            )}

            {/* Idle state */}
            {phase === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center py-10"
              >
                <div className="text-center">
                  <SparklesIcon className="w-8 h-8 text-tiber-500 mx-auto mb-2" />
                  <p className="text-white/60 text-sm">
                    {isSpanish ? "Iniciando demo..." : "Starting demo..."}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroChatDemo;
